import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  AddCircleOutline,
  Delete,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import PayrollViewController from "../../redux/actions/payrollApiAction";
import PayRollFormModal from "./AddPayRoll";
import PayrollView from "./ViewPayRoll";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import EmployeePayrollView from "./ViewEmployeePayroll";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ListPageLayout from "../../components/layout/ListPageLayout";
import PayrollMobileView from "./PayrollMobileView";
import AddSalaryReportMonth from "./AddReportmonth";
import usePermission from "../../utils/usePermission";
import {
  BRAND,
  toolbarButtonSx,
  inputSx,
  primaryButtonSx,
  dialogPaperSx,
} from "../../components/auth/authTheme";

import "../../styles/styles.css";
import "../../styles/layout.css";

const PayrollListPage = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { hasPermission } = usePermission();

  const { getEmployeePaySlip, deletePayroll, getEmployeePayroll } =
    PayrollViewController();
  const { payslips, payroll } = useSelector((state) => state.payslip);

  const [showModal, setShowModal] = useState(false);
  const [editPayroll, setEditPayroll] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [employeeViewOpen, setEmployeeViewOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [multipleEmployees, setMultipleEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const now = new Date();
  const currentMonth = String(now.getMonth() + 1);
  const currentYear = String(now.getFullYear());

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const canCreate = hasPermission("payrolls", "create");
  const canGenerateReport = hasPermission("payrolls", "generate_report");

  useEffect(() => {
    dispatch(getEmployeePayroll());
    dispatch(getEmployeePaySlip());
  }, [dispatch]);

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
    setSelectedRows([]);
    setSearchTerm("");
  };

  const handleDelete = useCallback(
    (id) => dispatch(deletePayroll(id)),
    [dispatch, deletePayroll]
  );

  const handleView = useCallback((payroll) => {
    setSelectedPayroll(payroll);
    setViewModalOpen(true);
  }, []);

  const handleEmployeeView = useCallback((employee) => {
    setSelectedEmployee(employee);
    setEmployeeViewOpen(true);
  }, []);

  const clearEmployeeSelection = () => {
    gridRef.current?.deselectAll();
    setSelectedRows([]);
    setShowSuccessDialog(false);
  };

  const handleToggleSelect = useCallback((employee) => {
    setSelectedRows((prev) => {
      const id = employee.employee_id || employee.id;
      const exists = prev.some(
        (row) => (row.employee_id || row.id) === id
      );
      if (exists) {
        return prev.filter((row) => (row.employee_id || row.id) !== id);
      }
      return [...prev, employee];
    });
  }, []);

  const handleMobileTabChange = useCallback((tabIndex) => {
    setActiveTab(tabIndex);
    setSelectedRows([]);
    setSearchTerm("");
  }, []);

  const displayedEmployees = useMemo(() => {
    const rows = payroll || [];
    if (!searchTerm.trim()) return rows;
    const query = searchTerm.toLowerCase();
    return rows.filter(
      (emp) =>
        emp.employee_name?.toLowerCase().includes(query) ||
        String(emp.emp_id || "").toLowerCase().includes(query)
    );
  }, [payroll, searchTerm]);

  const filteredPayslips = useMemo(() => {
    const rows = payslips || [];
    return rows.filter((item) => {
      if (!item.month) return false;
      const date = new Date(item.month);
      const matchesMonth = selectedMonth
        ? date.getMonth() + 1 === parseInt(selectedMonth, 10)
        : true;
      const matchesYear = selectedYear
        ? date.getFullYear() === parseInt(selectedYear, 10)
        : true;
      const matchesSearch = `${item.employee_name || ""} ${item.emp_id || ""}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesMonth && matchesYear && matchesSearch;
    });
  }, [payslips, selectedMonth, selectedYear, searchTerm]);

  const yearOptions = useMemo(
    () => Array.from({ length: 5 }, (_, i) => parseInt(currentYear, 10) - 2 + i),
    [currentYear]
  );

  const presentDaysHeader = payroll?.[0]?.last_month
    ? `${payroll[0].last_month} Present`
    : "Present Days";

  const employeeColumnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR No.",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Employee ID",
        field: "emp_id",
        width: 110,
        maxWidth: 120,
        minWidth: 110,
        suppressSizeToFit: true,
      },
      {
        headerName: "Employee Name",
        field: "employee_name",
        flex: 1,
        minWidth: 160,
        cellClass: "ag-cell-ellipsis",
      },
      {
        headerName: "Basic Salary",
        field: "basic_salary",
        width: 120,
        maxWidth: 130,
        minWidth: 120,
        suppressSizeToFit: true,
      },
      {
        headerName: presentDaysHeader,
        field: "present_days_last_month",
        width: 150,
        maxWidth: 160,
        minWidth: 150,
        suppressSizeToFit: true,
        valueGetter: (params) => params.data.present_days_last_month ?? "N/A",
      },
      {
        headerName: "Actions",
        field: "actions",
        pinned: "right",
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() => handleEmployeeView(params.data)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [presentDaysHeader, handleEmployeeView]
  );

  const payrollColumnDefs = useMemo(
    () => [
      {
        headerName: "SR No.",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Employee",
        field: "employee_name",
        flex: 1,
        minWidth: 160,
        cellClass: "ag-cell-ellipsis",
      },
      {
        headerName: "Month",
        field: "month",
        flex: 1,
        minWidth: 140,
        valueGetter: (params) => {
          if (!params.data.month) return "N/A";
          const date = new Date(params.data.month);
          return date.toLocaleString("default", {
            month: "long",
            year: "numeric",
          });
        },
      },
      {
        headerName: "Net Salary",
        flex: 1,
        minWidth: 120,
        valueGetter: (params) =>
          params.data.salary?.net_salary?.toFixed(2) ?? "N/A",
      },
      {
        headerName: "Generated At",
        flex: 1,
        minWidth: 160,
        valueGetter: (params) => {
          if (!params.data.generated_at) return "N/A";
          return new Date(params.data.generated_at).toLocaleString("en-IN");
        },
      },
      {
        headerName: "Actions",
        field: "actions",
        pinned: "right",
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <Tooltip title="View">
              <IconButton size="small" onClick={() => handleView(params.data)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                sx={{ color: "#d32f2f" }}
                onClick={() => handleDelete(params.data.id)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleView, handleDelete]
  );

  const openAddPayroll = () => {
    setShowSuccessDialog(false);
    if (selectedRows.length === 1) {
      setEditPayroll(selectedRows[0]);
      setMultipleEmployees([]);
    } else {
      setEditPayroll(null);
      setMultipleEmployees(selectedRows);
    }
    setShowModal(true);
  };

  const monthFilter = (
    <FormControl size="small" sx={{ minWidth: 130 }}>
      <InputLabel id="payroll-month-label">Month</InputLabel>
      <Select
        labelId="payroll-month-label"
        value={selectedMonth}
        label="Month"
        onChange={(e) => setSelectedMonth(e.target.value)}
      >
        <MenuItem value="">All Months</MenuItem>
        {Array.from({ length: 12 }, (_, i) => (
          <MenuItem key={i + 1} value={String(i + 1)}>
            {new Date(0, i).toLocaleString("default", { month: "long" })}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const yearFilter = (
    <FormControl size="small" sx={{ minWidth: 100 }}>
      <InputLabel id="payroll-year-label">Year</InputLabel>
      <Select
        labelId="payroll-year-label"
        value={selectedYear}
        label="Year"
        onChange={(e) => setSelectedYear(e.target.value)}
      >
        {yearOptions.map((year) => (
          <MenuItem key={year} value={String(year)}>
            {year}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      {isMobile ? (
        <PayrollMobileView
          activeTab={activeTab}
          setActiveTab={handleMobileTabChange}
          employees={displayedEmployees}
          payslips={filteredPayslips}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRows={selectedRows}
          onToggleSelect={handleToggleSelect}
          onEmployeeView={handleEmployeeView}
          onPayslipView={handleView}
          onPayslipDelete={handleDelete}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          yearOptions={yearOptions}
          onAddPayroll={openAddPayroll}
          onGenerateReport={() => setShowGenerateModal(true)}
          canCreate={canCreate}
          canGenerateReport={canGenerateReport}
          presentDaysLabel={presentDaysHeader}
        />
      ) : (
      <ListPageLayout
        title="Payroll Processing"
        subtitle={
          activeTab === 0
            ? "Select employees and generate monthly payroll"
            : "Review generated payslips and salary reports"
        }
        headerAction={
          activeTab === 0 && canCreate ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutline />}
              disabled={selectedRows.length === 0}
              onClick={openAddPayroll}
              sx={toolbarButtonSx}
            >
              Add Payroll
              {selectedRows.length > 0 ? ` (${selectedRows.length})` : ""}
            </Button>
          ) : activeTab === 1 && canGenerateReport ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowGenerateModal(true)}
              sx={toolbarButtonSx}
            >
              Generate Salary Report
            </Button>
          ) : null
        }
        toolbarRight={
          <>
            {activeTab === 1 && (
              <>
                {monthFilter}
                {yearFilter}
              </>
            )}
            <TextField
              size="small"
              placeholder={
                activeTab === 0 ? "Search employees..." : "Search payrolls..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ ...inputSx, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <PageSizeSelector
              pageSize={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            />
          </>
        }
        tabs={
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            className="list-page-tabs"
          >
            <Tab label="Employee List" />
            <Tab label="Payroll List" />
          </Tabs>
        }
      >
        <div className="list-page-grid">
          {activeTab === 0 ? (
            <ReusableGrid
              rowData={displayedEmployees}
              columnDefs={employeeColumnDefs}
              gridRef={gridRef}
              pagination={true}
              pageSize={pageSize}
              getRowId={(params) =>
                String(params.data.employee_id || params.data.id)
              }
              onSelectionChanged={(rows) => setSelectedRows(rows)}
              emptyMessage="No employees found"
              emptyHint="Employees with salary details will appear here."
            />
          ) : (
            <ReusableGrid
              rowData={filteredPayslips}
              columnDefs={payrollColumnDefs}
              gridRef={gridRef}
              pagination={true}
              pageSize={pageSize}
              getRowId={(params) => String(params.data.id)}
              emptyMessage="No payroll records found"
              emptyHint="Generate payroll from the Employee List tab or adjust the month filter."
            />
          )}
        </div>
      </ListPageLayout>
      )}

      {showModal && (
        <PayRollFormModal
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setEditPayroll(null);
            setMultipleEmployees([]);
          }}
          onSuccess={() => setShowSuccessDialog(true)}
          initialData={editPayroll}
          multipleData={multipleEmployees}
        />
      )}

      {showGenerateModal && (
        <AddSalaryReportMonth
          show={showGenerateModal}
          handleClose={() => setShowGenerateModal(false)}
        />
      )}

      <PayrollView
        show={viewModalOpen}
        handleClose={() => setViewModalOpen(false)}
        payroll={selectedPayroll}
      />

      <EmployeePayrollView
        show={employeeViewOpen}
        handleClose={() => setEmployeeViewOpen(false)}
        payroll={selectedEmployee}
      />

      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            ...dialogPaperSx,
            mx: isMobile ? 2 : undefined,
            borderRadius: isMobile ? "12px" : dialogPaperSx?.borderRadius,
          },
        }}
      >
        <DialogTitle sx={{ color: BRAND.navy, fontWeight: 700 }}>
          Payroll Generated
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: BRAND.text }}>
            Payroll was generated for{" "}
            <strong>{selectedRows.length}</strong> employee
            {selectedRows.length > 1 ? "s" : ""}. Would you like to clear the
            current selection?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: isMobile ? 2 : 3,
            pb: isMobile ? 2 : 2.5,
            pt: 0,
            flexDirection: isMobile ? "column" : "row",
            gap: 1,
          }}
        >
          <Button
            onClick={clearEmployeeSelection}
            variant="contained"
            fullWidth={isMobile}
            sx={{ ...primaryButtonSx, ...(isMobile && { minHeight: 48, borderRadius: "12px" }) }}
          >
            Clear Selection
          </Button>
          <Button
            onClick={() => setShowSuccessDialog(false)}
            color="inherit"
            fullWidth={isMobile}
            sx={
              isMobile
                ? { minHeight: 44, textTransform: "none", fontWeight: 600 }
                : undefined
            }
          >
            Keep Selection
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PayrollListPage;
